import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { assertFirebaseAuth } from '../firebase';
import { AuthError, mapFirebaseAuthError } from './errors';
import {
  actionCodeSettings,
  isEmailVerifiedInIndex,
  signInUrl,
  normalizeEmail,
  syncEmailVerified,
  upsertEmailIndex,
} from './profile';

/** Apply email-verification oobCode from the auth-action link. */
export async function applyEmailVerificationCode(oobCode: string): Promise<void> {
  try {
    const auth = assertFirebaseAuth();
    if (!oobCode) {
      throw new AuthError('invalid_token', 'Invalid or expired verification link');
    }
    const info = await checkActionCode(auth, oobCode);
    const email = normalizeEmail(info.data.email || '');
    await applyActionCode(auth, oobCode);
    if (email) await upsertEmailIndex(email, true);
    const current = auth.currentUser;
    if (current) {
      await current.reload();
      await syncEmailVerified(current);
    }
  } catch (error) {
    if (error instanceof AuthError) throw error;
    throw mapFirebaseAuthError(error);
  }
}

/**
 * Send password-reset email only for verified accounts.
 * Unverified signups are tracked in `emailIndex` and must not get a reset link.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const normalized = normalizeEmail(email);
  try {
    const verified = await isEmailVerifiedInIndex(normalized);
    if (verified === false) {
      throw new AuthError(
        'email_not_verified',
        'Verify your email before resetting your password',
      );
    }

    const auth = assertFirebaseAuth();
    await sendPasswordResetEmail(
      auth,
      normalized,
      actionCodeSettings(signInUrl()),
    );
  } catch (error) {
    if (error instanceof AuthError) throw error;
    const code =
      error && typeof error === 'object' && 'code' in error
        ? String((error as { code: string }).code)
        : '';
    if (code === 'auth/user-not-found') {
      throw new AuthError(
        'account_not_found',
        'No account found for this email',
      );
    }
    throw mapFirebaseAuthError(error);
  }
}

export async function resetPasswordWithToken(
  oobCode: string,
  newPassword: string,
): Promise<void> {
  try {
    const auth = assertFirebaseAuth();
    if (!oobCode) {
      throw new AuthError('invalid_token', 'Invalid or expired reset link');
    }
    await confirmPasswordReset(auth, oobCode, newPassword);
  } catch (error) {
    if (error instanceof AuthError) throw error;
    throw mapFirebaseAuthError(error);
  }
}

export async function validateResetToken(oobCode: string): Promise<boolean> {
  if (!oobCode) return false;
  try {
    const auth = assertFirebaseAuth();
    await verifyPasswordResetCode(auth, oobCode);
    return true;
  } catch {
    return false;
  }
}
